import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Users, FileText, Check, CheckCircle, AlertCircle, MapPin, Clock, X, ChevronLeft, CreditCard, Phone } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const PRIMARY_COLOR = 'var(--theme-primary)';

// Payment Modal Component
const PaymentModal = ({ isOpen, onClose, amount, onSuccess, token, primaryColor }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: methodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (methodError) {
        setError(methodError.message);
        setProcessing(false);
        return;
      }

      // Create payment intent on backend
      const response = await axios.post(
        `${API_BASE_URL}/api/create-payment-intent`,
        {
          amount: Math.round(amount * 100), // Convert to cents
          currency: 'usd',
          payment_method_id: paymentMethod.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.data.success) {
        setError(response.data.error || 'Payment processing failed');
        setProcessing(false);
        return;
      }

      const { client_secret } = response.data;

      // Confirm payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(client_secret);

      if (confirmError) {
        setError(confirmError.message);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent.id);
        onClose();
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error || err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Required
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              A token payment of <span className="font-semibold">${amount.toFixed(2)}</span> is required to secure your reservation.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Details
            </label>
            <div className="border border-gray-300 rounded-lg p-3">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#424770',
                      '::placeholder': {
                        color: '#aab7c4',
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!stripe || processing}
              className="flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ReserveTable = () => {
  const navigate = useNavigate();
  
  // Step management
  const [currentStep, setCurrentStep] = useState(0);
  
  // Settings and data
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [formFields, setFormFields] = useState({});
  
  // Search criteria
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [guestCount, setGuestCount] = useState(2);
  const [categoryId, setCategoryId] = useState('');
  
  // Availability results
  const [availabilityResult, setAvailabilityResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Customer form data
  const [customerData, setCustomerData] = useState({});
  
  // Payment and confirmation
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmedReservation, setConfirmedReservation] = useState(null);
  const [showNoAvailabilityModal, setShowNoAvailabilityModal] = useState(false);
  const [noAvailabilityMessage, setNoAvailabilityMessage] = useState('');
  
  // Messages
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Business timezone and current time
  const [businessTimezone, setBusinessTimezone] = useState('America/New_York');
  const [businessCurrentDate, setBusinessCurrentDate] = useState('');
  const [businessCurrentTime, setBusinessCurrentTime] = useState('');
  
  // Get auth token
  const token = localStorage.getItem('token');
  
  useEffect(() => {
    fetchBusinessInfo();
    fetchSettings();
    fetchCategories();
    fetchFormFields();
  }, []);
  
  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      if (response.data) {
        setBusinessTimezone(response.data.timezone || 'America/New_York');
        // Extract current date and time from business current_time
        if (response.data.current_time) {
          const [datePart, timePart] = response.data.current_time.split('T');
          setBusinessCurrentDate(datePart);
          setBusinessCurrentTime(timePart.substring(0, 5)); // HH:mm
        }
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-settings/public`);
      if (response.data?.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-categories/public`);
      if (response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  const fetchFormFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/table-form-fields`);
      if (response.data?.fields) {
        setFormFields(response.data.fields);
        
        // Initialize dropdown fields with first option as default
        const initialData = {};
        response.data.fields.forEach(field => {
          if (field.field_type === 'dropdown' && field.options && field.options.length > 0) {
            initialData[field.id] = field.options[0];
          }
        });
        
        if (Object.keys(initialData).length > 0) {
          setCustomerData(prevData => ({...prevData, ...initialData}));
        }
      }
    } catch (error) {
      console.error('Error fetching form fields:', error);
    }
  };
  
  // Generate next 7 days
  const getNext7Days = () => {
    const days = [];
    
    // If we don't have business current date yet, use browser's date as fallback
    if (!businessCurrentDate || !businessCurrentTime || !settings) {
      const today = new Date();
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        days.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
        });
      }
      return days;
    }
    
    // Parse business current date
    const [year, month, day] = businessCurrentDate.split('-').map(Number);
    const todayInBusinessTZ = new Date(year, month - 1, day);
    
    // Check if business hours are over for today
    const endTime = settings.end_time || '22:00';
    const lastSlotBuffer = settings.last_slot_buffer || 0;
    const reservationDuration = settings.reservation_duration || 60;
    
    // Calculate the last bookable time (end_time - buffer - duration)
    const [endHour, endMin] = endTime.split(':').map(Number);
    const endMinutes = endHour * 60 + endMin;
    const lastBookableMinutes = endMinutes - lastSlotBuffer - reservationDuration;
    const lastBookableTime = `${String(Math.floor(lastBookableMinutes / 60)).padStart(2, '0')}:${String(lastBookableMinutes % 60).padStart(2, '0')}`;
    
    // Check if current time is past the last bookable time
    const isBusinessHoursOver = businessCurrentTime >= lastBookableTime;
    
    // Start from today or tomorrow based on whether business hours are over
    const startOffset = isBusinessHoursOver ? 1 : 0;
    
    for (let i = startOffset; i < startOffset + 7; i++) {
      const date = new Date(todayInBusinessTZ);
      date.setDate(todayInBusinessTZ.getDate() + i);
      days.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
      });
    }
    return days;
  };
  
  // Helper to add minutes to time
  const addMinutesToTime = (timeStr, minutes) => {
    const [hour, min] = timeStr.split(':').map(Number);
    const date = new Date(2000, 0, 1, hour, min);
    date.setMinutes(date.getMinutes() + minutes);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Check if time falls within break period
  const isInBreakPeriod = (timeStr) => {
    if (!settings || !settings.break_enabled) return false;
    
    const breakStart = settings.break_start_time || '14:00';
    const breakEnd = settings.break_end_time || '15:00';
    
    return timeStr >= breakStart && timeStr < breakEnd;
  };

  // Check if time range overlaps with break period
  const overlapsWithBreak = (startTime, endTime) => {
    if (!settings || !settings.break_enabled) return false;
    
    const breakStart = settings.break_start_time || '14:00';
    const breakEnd = settings.break_end_time || '15:00';
    
    return startTime < breakEnd && endTime > breakStart;
  };

  // Generate time slots based on settings, excluding break periods
  const generateTimeSlots = () => {
    if (!settings) return [];
    
    const slots = [];
    const startTime = settings.start_time || '09:00';
    const endTime = settings.end_time || '22:00';
    const interval = settings.slot_duration || 30;
    const duration = settings.reservation_duration || 60;
    const buffer = settings.last_slot_buffer || 30;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    // Determine the actual start time
    let currentHour = startHour;
    let currentMin = startMin;
    
    // If booking for today, start from current time (rounded up to next slot)
    if (selectedDate === businessCurrentDate && businessCurrentTime) {
      const [currentH, currentM] = businessCurrentTime.split(':').map(Number);
      const currentMinutes = currentH * 60 + currentM;
      const startMinutes = startHour * 60 + startMin;
      
      // Use whichever is later: business start time or current time
      if (currentMinutes > startMinutes) {
        // Round up to next slot interval
        const nextSlotMinutes = Math.ceil(currentMinutes / interval) * interval;
        currentHour = Math.floor(nextSlotMinutes / 60);
        currentMin = nextSlotMinutes % 60;
      }
    }
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
      
      // Calculate when this reservation would end
      const reservationEnd = addMinutesToTime(timeStr, duration);
      
      // Check if reservation would end beyond closing time minus buffer
      const lastAllowedEnd = addMinutesToTime(endTime, -buffer);
      if (reservationEnd > lastAllowedEnd) {
        break; // Don't show slots that would go past buffer
      }
      
      // Check if this slot falls within break period or would overlap with break
      if (!isInBreakPeriod(timeStr) && !overlapsWithBreak(timeStr, reservationEnd)) {
        slots.push(timeStr);
      }
      
      currentMin += interval;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    
    return slots;
  };
  
  const handleCheckAvailability = async () => {
    if (!selectedDate || !selectedTime) {
      setMessage({ type: 'error', text: 'Please select both date and time' });
      return;
    }
    
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      const duration = settings?.reservation_duration || 60;
      const response = await axios.post(`${API_BASE_URL}/api/table-reservations/check-availability`, {
        date: selectedDate,
        time: selectedTime,
        guest_count: guestCount,
        category_id: categoryId || null,
        duration_minutes: duration
      });
      
      if (response.data.available) {
        setAvailabilityResult(response.data);
        setCurrentStep(1);
      } else {
        // Show no availability modal instead of inline error
        setNoAvailabilityMessage(response.data.message || 'No tables available for the selected date and time');
        setShowNoAvailabilityModal(true);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error checking availability. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCustomerFormSubmit = (e) => {
    e.preventDefault();
    setCurrentStep(2);
  };
  
  const handleBooking = async (skipPaymentCheck = false) => {
    const isPaymentEnabled = settings?.table_payment_enabled;
    const tokenAmount = Number(settings?.token_amount || 0);
    
    // Check if payment is required and not completed
    if (!skipPaymentCheck && isPaymentEnabled && tokenAmount > 0 && !paymentCompleted) {
      setShowPaymentModal(true);
      return;
    }
    
    setLoading(true);
    
    try {
      const tableIds = availabilityResult.allocated_tables.map(t => t.table_id);
      const tableNames = availabilityResult.allocated_tables.map(t => t.table_name).join(', ');
      
      const duration = settings?.reservation_duration || 60;
      const reservationData = {
        reservation_type: 'table',
        dropoff_date: selectedDate,
        dropoff_time: selectedTime,
        duration_minutes: duration,
        form_details: customerData,
        special_instructions: {},
        assigned_table_id: tableIds[0],
        assigned_table_name: tableNames,
        guest_count: guestCount,
        category_id: categoryId || null,
        payment_token_amount: isPaymentEnabled ? settings.token_amount : null,
        payment_transaction_id: skipPaymentCheck || paymentCompleted ? 'stripe_payment_' + Date.now() : null
      };
      
      const response = await axios.post(`${API_BASE_URL}/api/table-reservations`, reservationData);
      
      if (response.data.success) {
        setConfirmedReservation(response.data.reservation);
        setShowConfirmationModal(true);
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error creating reservation. Please try again.' });
    } finally {
      setLoading(false);
    }
  };
  
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };
  
  const steps = [
    { id: 0, title: 'Select Date & Time', icon: Calendar },
    { id: 1, title: 'Your Details', icon: Users },
    { id: 2, title: 'Review', icon: FileText }
  ];
  
  if (!settings) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(41,173,211)] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Reserve a Table</h2>
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`px-6 py-3 ${
              message.type === 'error' ? 'bg-red-50 border-b border-red-200' : 
              message.type === 'success' ? 'bg-green-50 border-b border-green-200' : 
              'bg-blue-50 border-b border-blue-200'
            }`}>
              <p className={`text-sm ${
                message.type === 'error' ? 'text-red-600' : 
                message.type === 'success' ? 'text-green-600' : 
                'text-theme-blue'
              }`}>
                {message.text}
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow-sm border-b border-gray-200">
            <nav className="-mb-px flex px-6" aria-label="Tabs">
              {steps.map((step) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <button
                    key={step.id}
                    className={`flex-1 py-4 px-1 border-b-2 font-medium text-sm flex items-center justify-center space-x-2 ${
                      isActive
                        ? 'border-theme-blue text-theme-blue'
                        : isCompleted
                        ? 'border-theme-blue text-theme-blue'
                        : 'border-transparent text-gray-500'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                    <span>{step.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Step Content */}
          <div className="p-6">
            {/* Step 0: Select Date & Time */}
            {currentStep === 0 && (
              <div>
                {/* Filters in One Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Date Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Select Date</label>
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-[rgb(41,173,211)] bg-white hover:border-gray-300 transition-colors appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      <option value="">Choose date...</option>
                      {getNext7Days().map(day => (
                        <option key={day.value} value={day.value}>{day.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Table Category Selection */}
                  {categories.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Table Category</label>
                      <select
                        value={categoryId}
                        onChange={(e) => setCategoryId(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-[rgb(41,173,211)] bg-white hover:border-gray-300 transition-colors appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.75rem center',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="">All</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Number of Guests */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Number of Guests</label>
                    <select
                      value={guestCount}
                      onChange={(e) => setGuestCount(parseInt(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-[rgb(41,173,211)] bg-white hover:border-gray-300 transition-colors appearance-none cursor-pointer"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        paddingRight: '2.5rem'
                      }}
                    >
                      {Array.from({ length: settings?.max_guests || 10 }, (_, i) => i + 1).map(num => (
                        <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Time Slot Selection */}
                {selectedDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Slot</label>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                      {generateTimeSlots().map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                            selectedTime === slot
                              ? 'bg-[rgb(41,173,211)] text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Continue Button */}
                {selectedDate && selectedTime && (
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleCheckAvailability}
                      disabled={loading}
                      className="px-8 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                      style={{ backgroundColor: PRIMARY_COLOR }}
                    >
                      {loading ? 'Checking...' : 'Continue'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Step 1: Customer Details */}
            {currentStep === 1 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Reservation Details - Right Side on Desktop, Top on Mobile */}
                <div className="lg:col-span-1 lg:order-2 order-1">
                  <div className="lg:sticky lg:top-6">
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm font-semibold text-gray-900 mb-3">✓ Tables Reserved</p>
                      <div className="space-y-2 pl-3">
                        {availabilityResult?.allocated_tables.map((table, idx) => (
                          <p key={idx} className="text-sm text-gray-700">
                            • {table.table_name} (Capacity: {table.capacity})
                          </p>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-700 pl-3">
                          • {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-sm text-gray-700 pl-3">
                          • {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </p>
                        <p className="text-sm text-gray-700 pl-3">
                          • {guestCount} {guestCount === 1 ? 'Guest' : 'Guests'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Fields - Left Side on Desktop, Bottom on Mobile */}
                <div className="lg:col-span-2 lg:order-1 order-2">
                  <form onSubmit={handleCustomerFormSubmit} className="space-y-4">
                  {/* Dynamic Form Fields */}
                  {Object.entries(formFields).length > 0 ? (
                    Object.entries(formFields).map(([key, field]) => (
                      <div key={key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {field.field_name} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        {field.description && (
                          <p className="text-xs text-gray-500 mb-1">{field.description}</p>
                        )}
                        {field.field_type === 'textarea' ? (
                          <textarea
                            value={customerData[key] || ''}
                            onChange={(e) => setCustomerData({ ...customerData, [key]: e.target.value })}
                            required={field.required}
                            placeholder={field.placeholder}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
                          />
                        ) : field.field_type === 'dropdown' ? (
                          <select
                            value={customerData[key] || ''}
                            onChange={(e) => setCustomerData({ ...customerData, [key]: e.target.value })}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
                          >
                            <option value="">Select...</option>
                            {field.options && field.options.map((opt, idx) => (
                              <option key={idx} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : field.field_type === 'radio' ? (
                          <div className="space-y-2">
                            {field.options && field.options.map((opt, idx) => (
                              <label key={idx} className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={key}
                                  value={opt}
                                  checked={customerData[key] === opt}
                                  onChange={(e) => setCustomerData({ ...customerData, [key]: e.target.value })}
                                  required={field.required}
                                  className="text-[rgb(41,173,211)]"
                                />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <input
                            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
                            value={customerData[key] || ''}
                            onChange={(e) => setCustomerData({ ...customerData, [key]: e.target.value })}
                            required={field.required}
                            placeholder={field.placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)]"
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No form fields configured yet.</p>
                      <p className="text-sm mt-2">Please contact the administrator to set up the booking form.</p>
                    </div>
                  )}
                  
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(0)}
                        className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-3 text-white font-semibold rounded-lg"
                        style={{ backgroundColor: PRIMARY_COLOR }}
                      >
                        Continue
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Step 2: Review */}
            {currentStep === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl">
                {/* Date & Time */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Reservation Date
                  </div>
                  <div className="text-base font-semibold text-gray-900">
                    {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                </div>

                {/* Guest Count */}
                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                  <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Party Size
                  </div>
                  <div className="text-base font-semibold text-gray-900">{guestCount} {guestCount === 1 ? 'Guest' : 'Guests'}</div>
                  {categoryId && (
                    <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {categories.find(c => c.id === categoryId)?.category_name}
                    </div>
                  )}
                </div>

                {/* Allocated Tables */}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200 md:col-span-2">
                  <div className="text-sm font-semibold text-blue-900 mb-3">Allocated Tables</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {availabilityResult?.allocated_tables.map((table, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-blue-100">
                        <span className="font-medium">{table.table_name}</span>
                        <span className="text-gray-600 text-xs">Cap: {table.capacity}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Form Details */}
                {Object.entries(customerData).length > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 md:col-span-2">
                    <div className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Your Details
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(customerData).map(([key, value]) => {
                        const field = formFields[key];
                        if (!value || !field) return null;
                        return (
                          <div key={key}>
                            <div className="text-xs font-medium text-gray-600 mb-1">{field.field_name}</div>
                            <div className="text-sm text-gray-900">{value}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-between mt-4 md:col-span-2">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </button>
                  <button
                    onClick={() => handleBooking(false)}
                    disabled={loading}
                    className="ml-auto px-6 py-2 text-white rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: PRIMARY_COLOR }}
                  >
                    {loading ? 'Processing...' : 'Reserve Now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={settings?.token_amount || 0}
          token={token}
          primaryColor={PRIMARY_COLOR}
          onSuccess={(paymentIntentId) => {
            setPaymentCompleted(true);
            setShowPaymentModal(false);
            showMessage('success', 'Payment completed successfully!');
            setTimeout(() => {
              handleBooking(true);
            }, 500);
          }}
        />
      )}

      {/* No Availability Modal */}
      {showNoAvailabilityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: PRIMARY_COLOR }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">No Tables Available</h3>
                <button
                  onClick={() => setShowNoAvailabilityModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Error Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-orange-600" />
                </div>
              </div>

              {/* Message */}
              <div className="text-center mb-6">
                <p className="text-gray-700 mb-2">
                  {noAvailabilityMessage}
                </p>
                <p className="text-sm text-gray-600">
                  Please try a different date or time slot.
                </p>
              </div>

              {/* Call to Action */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-gray-900 mb-3 text-center">
                  Need Help? Speak with our team
                </p>
                <a
                  href={`tel:${settings?.business_phone || '+1234567890'}`}
                  className="flex items-center justify-center gap-2 w-full py-3 text-white rounded-lg font-medium transition-colors"
                  style={{ backgroundColor: PRIMARY_COLOR }}
                >
                  <Phone className="h-5 w-5" />
                  {settings?.business_phone || '+1 (234) 567-890'}
                </a>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  We'll help you find the perfect table
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-center">
              <button
                onClick={() => setShowNoAvailabilityModal(false)}
                className="px-6 py-2 text-white rounded-lg font-medium"
                style={{ backgroundColor: PRIMARY_COLOR }}
              >
                Try Another Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && confirmedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: PRIMARY_COLOR }}>
              <div>
                <h3 className="text-lg font-semibold text-white">Reservation Confirmed</h3>
                {confirmedReservation.reservation_number && (
                  <p className="text-xs text-white text-opacity-90 mt-0.5">
                    #{confirmedReservation.reservation_number}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  window.location.href = '/';
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Success Message */}
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Your table reservation has been successfully booked.
                    </p>
                  </div>
                  {confirmedReservation.reservation_number && (
                    <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200 text-right">
                      <div className="text-xs font-medium text-blue-700 mb-0.5">Reservation Number</div>
                      <div className="text-xl text-blue-900 font-bold">{confirmedReservation.reservation_number}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reservation Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200 shadow-sm">
                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Date & Time
                  </div>
                  <div className="text-sm font-semibold text-gray-900">
                    {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200 shadow-sm">
                  <div className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    Party Details
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{guestCount} {guestCount === 1 ? 'Guest' : 'Guests'}</div>
                  {availabilityResult?.allocated_tables && (
                    <div className="text-sm text-gray-700 mt-1">
                      Table: {availabilityResult.allocated_tables.map(t => t.table_name).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowConfirmationModal(false);
                  window.location.href = '/';
                }}
                className="px-6 py-2 text-white rounded-lg"
                style={{ backgroundColor: PRIMARY_COLOR }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReserveTable;
